import { parseCoviEntityUri } from "@/app-covi"
import Image from "@tiptap/extension-image"
import { TaskItem, TaskList } from "@tiptap/extension-list"
import { TableKit } from "@tiptap/extension-table"
import { MarkdownManager } from "@tiptap/markdown"
import type { JSONContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"

import type { Notebook, NotebookDocument } from "./notebookSnapshot"

export interface NotebookAppearance {
  background?: string
  color?: string
  font?: "sans" | "serif" | "mono"
  fontSize?: number
}
export interface NotebookPlotSpec {
  kind: "scatter" | "bar" | "line"
  title?: string
  caption?: string
  xLabel?: string
  yLabel?: string
  leftLabel?: string
  rightLabel?: string
  topLabel?: string
  bottomLabel?: string
  points: Array<{ label: string; x: number; y: number }>
}
export const NOTEBOOK_FONTS = {
  sans: "var(--font-sans, sans-serif)",
  serif: "Georgia, serif",
  mono: "var(--font-mono, monospace)",
}

/** Editor defaults are absent values, not authored formatting. */
export function cleanCanvas(node: JSONContent): JSONContent {
  return {
    ...node,
    ...(node.attrs
      ? {
          attrs: Object.fromEntries(
            Object.entries(node.attrs).filter(([, value]) => value != null),
          ),
        }
      : {}),
    ...(node.marks
      ? {
          marks: node.marks.map((mark) => ({
            ...mark,
            ...(mark.attrs
              ? {
                  attrs: Object.fromEntries(
                    Object.entries(mark.attrs).filter(
                      ([, value]) => value != null,
                    ),
                  ),
                }
              : {}),
          })),
        }
      : {}),
    ...(node.content ? { content: node.content.map(cleanCanvas) } : {}),
  }
}
const markdown = new MarkdownManager({
  extensions: [StarterKit, Image, TableKit, TaskList, TaskItem],
})
function citations(node: JSONContent): JSONContent[] {
  if (node.type !== "text") {
    return [
      {
        ...node,
        ...(node.content ? { content: node.content.flatMap(citations) } : {}),
      },
    ]
  }
  if (
    node.marks?.some((mark) => mark.type === "code" || mark.type === "link")
  ) {
    return [node]
  }
  const text = node.text ?? "",
    result: JSONContent[] = []
  let offset = 0
  for (const match of text.matchAll(/coval:\/\/[a-z_]+\/[^\s)\]}"'<>]+/gi)) {
    const href = match[0].replace(/[.,;:!]+$/, ""),
      ref = parseCoviEntityUri(href)
    if (!ref) {
      continue
    }
    if (match.index > offset) {
      result.push({ ...node, text: text.slice(offset, match.index) })
    }
    result.push({
      ...node,
      text: ref.id,
      marks: [...(node.marks ?? []), { type: "link", attrs: { href } }],
    })
    offset = match.index + href.length
  }
  if (offset < text.length) {
    result.push({ ...node, text: text.slice(offset) })
  }
  return result
}
const prose = (text: string): JSONContent[] =>
  (markdown.parse(text).content ?? []).flatMap(citations)
const heading = (text: string, level: number): JSONContent => ({
  type: "heading",
  attrs: { level },
  content: [{ type: "text", text }],
})

/** Legacy sources become ordinary document nodes only when the owner edits. */
export function notebookCanvas(notebook: Notebook): JSONContent {
  if (notebook.document?.content) {
    return notebook.document.content
  }
  const document = notebook.document
  const nodes: JSONContent[] = []
  if (
    document &&
    (document.title !== "Untitled notebook" ||
      document.sections.length ||
      document.summary)
  ) {
    nodes.push(heading(document.title, 1))
  }
  if (document?.summary) {
    nodes.push(...prose(document.summary))
  }
  const embedded = new Set<number>()
  for (const section of document?.sections ?? []) {
    if (section.heading) {
      nodes.push(heading(section.heading, 2))
    }
    nodes.push(...prose(section.body))
    for (const index of section.embed_cells) {
      nodes.push({ type: "evidence", attrs: { index } })
      embedded.add(index)
    }
  }
  if (document?.caveats.length) {
    nodes.push(heading("Caveats", 2), {
      type: "bulletList",
      content: document.caveats.map((caveat) => ({
        type: "listItem",
        content: prose(caveat),
      })),
    })
  }
  for (const [index, cell] of notebook.snapshot.cells.entries()) {
    if (
      (cell.kind === "table" || cell.kind === "chart") &&
      !embedded.has(index) &&
      !document?.excluded_cells?.includes(index)
    ) {
      nodes.push({ type: "evidence", attrs: { index } })
    }
    if (!document && cell.kind === "text") {
      nodes.push(...prose(cell.text))
    }
  }
  return {
    type: "doc",
    content: nodes.length ? nodes : [{ type: "paragraph" }],
  }
}
/** Media nodes require the document marker so stale bundles stay read-only. */
const containsMediaNodes = (node: JSONContent): boolean =>
  node.type === "image" ||
  node.type === "video" ||
  (node.content ?? []).some(containsMediaNodes)
export function withCanvas(
  notebook: Notebook,
  content: JSONContent,
  appearance: NotebookAppearance,
): NotebookDocument {
  // Legacy prose has become editable nodes. Retaining a second hidden copy
  // would resurrect deleted text in older readers and authoring paths.
  return {
    version: 1,
    title: notebook.document?.title ?? notebook.title,
    summary: "",
    sections: [],
    caveats: [],
    excluded_cells: notebook.document?.excluded_cells ?? [],
    content,
    appearance,
    ...(containsMediaNodes(content) || notebook.document?.media_version
      ? { media_version: 1 }
      : {}),
  }
}
